import { ComponentType } from 'react';

import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import WalletGoldIcon from '@/assets/icons/wallet-gold';
import WalletUsdIcon from '@/assets/icons/wallet-usd';
import { ds } from '@/components/lib/design.system';
import { productNames } from '@/hooks/use-active-product-name';
import { Product } from '@/hooks/use-jurisdiction';
import { SocialCurrency } from '@/hooks/use-social-currency';
import { Currency as PickemCurrency } from '@/types/api.generated';
import { Wallet } from '@/utils/fetch-wallet-balance';
import { toCoinsCurrency, toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';
import i18n from 'i18next';

export const CARD_WIDTH = 361;
export const CARD_HEIGHT = 180;

export type CardItem = {
    currency: Currency;
    info: CardContentInfo;
    style: CardTheme;
    amount: string;
};

export type SocialCurrencyIconProps = {
    size?: number;
};

export const BONUS_CURRENCY = 'BONUS';
export type BonusCurrency = typeof BONUS_CURRENCY;
export type Currency = SocialCurrency | PickemCurrency | BonusCurrency;

export type CardTheme = {
    cardColor: string;
    textColor: string;
    icon?: ComponentType<SocialCurrencyIconProps>;
};

export type CardContentInfo = {
    title: string;
    touchpointText?: string;
    description: string;
    primaryButtonText: string;
    secondaryButtonText?: string;
};

type ProductWalletData = Partial<Record<string, Wallet>>;

type CurrencyConfig = {
    theme: CardTheme;
    getInfo: (wallet: ProductWalletData, product: Product) => CardContentInfo;
    getAmount: (wallet: ProductWalletData) => string;
};

const currencyConfigs: Record<Currency, CurrencyConfig> = {
    [SocialCurrency.COINS]: {
        theme: {
            cardColor: ds.colorValue.background.accent.yellow.subtle,
            textColor: ds.colorValue.text.primary,
            icon: WalletGoldIcon,
        },
        getInfo: () => ({
            title: i18n.t('sweeps:gold_coins'),
            touchpointText: i18n.t('common:learn_more'),
            description: i18n.t('common:play_for_fun'),
            primaryButtonText: i18n.t('wallet:purchase'),
            secondaryButtonText: i18n.t('wallet:redeem'),
        }),
        getAmount: wallet => toCoinsCurrency(wallet.COINS?.total || '0'),
    },
    [SocialCurrency.CASH]: {
        theme: {
            cardColor: ds.colorValue.background.accent.green.subtle,
            textColor: ds.colorValue.text.primary,
            icon: WalletCashIcon,
        },
        getInfo: wallet => ({
            title: i18n.t('sweeps:currency_cash'),
            touchpointText: i18n.t('common:learn_more'),
            description: i18n.t('wallet:redeemable', {
                amount: wallet.CASH?.real_amount || '0',
            }),
            primaryButtonText: i18n.t('wallet:redeem'),
            secondaryButtonText: i18n.t('wallet:purchase'),
        }),
        getAmount: wallet =>
            toSocialCurrency(Number(wallet.CASH?.real_amount) + Number(wallet.CASH?.bonus_amount) || '0'),
    },
    [BONUS_CURRENCY]: {
        theme: {
            cardColor: ds.colorValue.background.accent.purple.subtle,
            textColor: ds.colorValue.text.primary,
            icon: WalletBetrBucksIcon,
        },
        getInfo: () => ({
            title: i18n.t('common:bonus'),
            touchpointText: i18n.t('common:learn_more'),
            description: i18n.t('common:non_redeemable'),
            primaryButtonText: i18n.t('wallet:purchase'),
            secondaryButtonText: undefined,
        }),
        getAmount: wallet => toSocialCurrency(Number(wallet.CASH?.free_amount) || 0, false) || '0',
    },
    [PickemCurrency.Usd]: {
        theme: {
            cardColor: ds.colorValue.background.accent.green.subtle,
            textColor: ds.colorValue.text.primary,
            icon: WalletUsdIcon,
        },
        getInfo: wallet => ({
            title: i18n.t('wallet:usd'),
            touchpointText: undefined,
            description: i18n.t('wallet:withdrawable_amount', {
                amount: toLocaleCurrency(Number(wallet?.USD?.real_amount), false) || '$0.00',
            }),
            primaryButtonText: i18n.t('wallet:deposit'),
            secondaryButtonText: undefined,
        }),
        getAmount: wallet =>
            toLocaleCurrency(Number(wallet?.USD?.real_amount) + Number(wallet?.USD?.bonus_amount), false) || '$0.00',
    },
    [PickemCurrency.Fre]: {
        theme: {
            cardColor: ds.colorValue.background.accent.purple.subtle,
            textColor: ds.colorValue.text.primary,
            icon: WalletBetrBucksIcon,
        },
        getInfo: (_, product) => ({
            title: i18n.t('wallet:betr_bucks'),
            touchpointText: i18n.t('common:learn_more'),
            description:
                product === Product.Prediction || product === Product.Rips
                    ? i18n.t('wallet:unavailable_on_product', { product: productNames[product] })
                    : i18n.t('wallet:non_withdrawable'),
            primaryButtonText: i18n.t('transactions:earn_betr_bucks'),
            secondaryButtonText: i18n.t('wallet:add_to_wallet'),
        }),
        getAmount: wallet => toLocaleCurrency(Number(wallet?.USD?.free_amount), false) || '0.00',
    },
};

export const SOCIAL_WALLET_CURRENCIES: Currency[] = [SocialCurrency.COINS, SocialCurrency.CASH, BONUS_CURRENCY];
export const PICKEM_CURRENCIES: Currency[] = [PickemCurrency.Usd, PickemCurrency.Fre];

export const getCurrenciesForProduct = (product: Product): Currency[] => {
    if (product === Product.Sweeps || product === Product.Casino) {
        return SOCIAL_WALLET_CURRENCIES;
    }
    return PICKEM_CURRENCIES;
};

/** For a given list of currencies and wallet data, build the corresponding list of CardItems for display in the wallet screen */
export const buildCardItems = (currencies: Currency[], wallet: ProductWalletData, product: Product): CardItem[] => {
    return (
        currencies
            // In social wallet, only show the Bonus (FRE) card when there is a balance - Refer to CSP-2184.
            .filter(currency => currency !== BONUS_CURRENCY || Number(wallet?.CASH?.free_amount) > 0)
            .map(currency => {
                const config = currencyConfigs[currency];
                const amount = config.getAmount(wallet);
                const info = config.getInfo(wallet, product);
                return {
                    currency,
                    info,
                    style: config.theme,
                    amount,
                };
            })
    );
};
